// What the page can write, and which of those this browser will actually encode. Nothing is assumed: a format is
// offered only once a one-pixel canvas has come back holding it, since a `toBlob` a browser does not know answers
// with a PNG under the name that was asked for rather than with a refusal.

export interface OutputFormat {
  value: string;
  label: string;
  mime: string;
  extension: string;
  lossy: boolean;
  alpha: boolean;
}

export const FORMATS: OutputFormat[] = [
  { value: "png", label: "PNG", mime: "image/png", extension: "png", lossy: false, alpha: true },
  { value: "jpeg", label: "JPEG", mime: "image/jpeg", extension: "jpg", lossy: true, alpha: false },
  { value: "webp", label: "WebP", mime: "image/webp", extension: "webp", lossy: true, alpha: true },
  { value: "avif", label: "AVIF", mime: "image/avif", extension: "avif", lossy: true, alpha: true },
];

export function formatFor(value: string): OutputFormat {
  return FORMATS.find((format) => format.value === value) ?? FORMATS[0];
}

// The format a file already is, so a picture that is only being cropped comes back as what it arrived as.
export function formatForMime(mime: string): OutputFormat | null {
  return FORMATS.find((format) => format.mime === mime) ?? null;
}

// What a picture comes back as until somebody chooses: its own format, or PNG where this browser cannot write that.
export function arrivedFormat(mime: string, offered: OutputFormat[]): OutputFormat {
  const own = formatForMime(mime);
  return own && offered.some((format) => format.value === own.value) ? own : FORMATS[0];
}

export function encodable(): OutputFormat[] {
  const canvas = document.createElement("canvas");
  canvas.width = canvas.height = 1;
  return FORMATS.filter((format) => {
    if (format.value === "png") return true;
    try {
      return canvas.toDataURL(format.mime).startsWith(`data:${format.mime}`);
    } catch {
      return false;
    }
  });
}
